import React from 'react';
import {
  PlayIcon,
  VideoCameraIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from 'recharts';
import {
  Tooltip as UITooltip,
  TooltipContent as UITooltipContent,
  TooltipProvider as UITooltipProvider,
  TooltipTrigger as UITooltipTrigger,
} from 'src/components/ui/ui/tooltip';

/**
 * The "Analysis & Transcript" body of the interview replay modal: recording
 * player, per-question analysis, full transcript, and the three AI score cards.
 *
 * Extracted VERBATIM from ScreeningDetailPage, where it lived inline. The public
 * hiring-manager review page previously rendered a completely separate modal
 * (pages/results/components/InterviewSessionModal), so the two surfaces had
 * independently-written question analysis and transcript views that had drifted
 * apart in layout and detail. Both now render this one component, so they cannot
 * drift again.
 *
 * Deliberately presentational and prop-driven — it owns no fetching and no
 * session state. That is what lets the recruiter page drive it from authenticated
 * endpoints while the public page drives the same markup from share-code-scoped
 * ones.
 *
 * NOT extracted: the recruiter modal's Proctoring Details tab (~2,200 further
 * lines). It stays inline in ScreeningDetailPage because proctoring is recruiter-
 * only and is never shown on a share link, so sharing it would buy nothing.
 */

/* ───── Deduplicate Transcript Turns ───── */
// Moved here with the markup that is its only consumer.
export const deduplicateTranscriptTurns = (turns) => {
  if (!turns || !Array.isArray(turns)) return [];
  const norm = (s) =>
    (s || '')
      .replace(/[.,/#!$%^&*;:{}=\-_`~()?]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  const deduped = [];
  for (const turn of turns) {
    const text = typeof turn.text === 'string' ? turn.text : (turn.text?.text || turn.content || turn.message || '');
    const turnNorm = norm(text);
    if (!turnNorm) continue;
    let isDup = false;
    const start = Math.max(0, deduped.length - 5);
    for (let i = deduped.length - 1; i >= start; i--) {
      const prev = deduped[i];
      if (prev.role !== turn.role) continue;
      const prevText = typeof prev.text === 'string' ? prev.text : (prev.text?.text || prev.content || prev.message || '');
      const prevNorm = norm(prevText);
      if (prevNorm === turnNorm) { isDup = true; break; }
      if (prevNorm.length >= 15 && turnNorm.length >= 15) {
        if (prevNorm.includes(turnNorm)) { isDup = true; break; }
        if (turnNorm.includes(prevNorm)) {
          deduped[i] = { ...deduped[i], text: turn.text };
          isDup = true;
          break;
        }
      }
    }
    if (!isDup) deduped.push(turn);
  }
  return deduped;
};

/**
 * @param {object}   session          Raw replay session (recording flags, candidateName, startedAt, transcript).
 * @param {object}   analysisSession  Session the SCORES are read from. Differs from `session` only under the
 *                                    recruiter's scoring-compare dev tool; callers without it pass the same object.
 * @param {object}   interviewDetails Interview config; only `rubrics` is used, to label sections on older sessions.
 * @param {string}   candidateNameFallback   Shown when the session carries no candidateName.
 * @param {string}   interviewTitleFallback  Shown under the candidate name on the video poster.
 * @param {string}   recordingUrl     Resolved playable URL, or falsy to show the play button.
 * @param {boolean}  recordingLoading Spinner state for the play button.
 * @param {Function} onFetchRecording Called when the reviewer clicks play. The CALLER owns which endpoint is hit —
 *                                    this is what keeps the component identical on authenticated and public pages.
 * @param {object}   videoRef         Ref forwarded to the <video>/<audio> element.
 * @param {string}   innerTab         'questions' | 'transcript'
 * @param {Function} onInnerTabChange
 * @param {Set}      expandedQuestions
 * @param {Function} onExpandedQuestionsChange
 * @param {Function} translateText    (string) => string, for per-question response strings. Identity when the
 *                                    caller has no translation to apply.
 * @param {Function} pickTurnText     (turn) => string, for transcript turn objects.
 * @param {Function} renderScoreCards Optional ({ overall, areas, strengths, improvements, redFlags, standout }) => node,
 *                                    replacing the three score cards (Application Form screenings use it).
 */
export default function InterviewAnalysisTranscript({
  session,
  analysisSession,
  interviewDetails = null,
  candidateNameFallback = '',
  interviewTitleFallback = '',
  recordingUrl = null,
  recordingLoading = false,
  onFetchRecording,
  videoRef = null,
  innerTab = 'questions',
  onInnerTabChange,
  expandedQuestions,
  onExpandedQuestionsChange,
  translateText,
  pickTurnText,
  renderScoreCards,
}) {
  if (!session) return null;

  // Callers that have no scoring-compare mode pass only `session`.
  const scored = analysisSession || session;
  const expanded = expandedQuestions instanceof Set ? expandedQuestions : new Set();
  const toggleExpanded = (key) => {
    const next = new Set(expanded);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    if (typeof onExpandedQuestionsChange === 'function') onExpandedQuestionsChange(next);
  };
  const asText = (v) => (typeof v === 'string' ? v : (v?.text?.text || v?.content || v?.message || ''));
  const renderTurnText = (turn) =>
    typeof pickTurnText === 'function' ? pickTurnText(turn) : asText(turn?.text ?? turn);
  const renderResponse = (response) =>
    typeof translateText === 'function' ? translateText(response) : response;

  return (
    <div className="space-y-6">

      {/* TOP: Video Player + Inner Tabs */}
      <div className="flex flex-col xl:flex-row gap-5 xl:items-stretch" style={{ minHeight: '340px' }}>

        {/* LEFT: Video Player */}
        <div className="w-full xl:basis-[40%] xl:flex-none bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-900 rounded-2xl overflow-hidden flex flex-col relative shadow-2xl flex-shrink-0 border border-white/10 ring-1 ring-black/40" style={{ minHeight: '260px' }}>
          <div className="flex-1 flex items-center justify-center relative">
            {session.hasRecording ? (
              recordingUrl ? (
                session.recordingType === 'video' ? (
                  <video ref={videoRef} src={recordingUrl} controls autoPlay className="w-full h-full object-contain bg-black" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-800 p-8">
                    <audio ref={videoRef} src={recordingUrl} controls autoPlay className="w-full" />
                  </div>
                )
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof onFetchRecording === 'function') onFetchRecording();
                    }}
                    disabled={recordingLoading}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-xl transition-transform hover:scale-110 disabled:opacity-50"
                  >
                    {recordingLoading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <PlayIcon className="w-8 h-8 ml-1" />
                    )}
                  </button>
                  <p className="mt-4 text-sm font-medium text-slate-300">
                    {session.recordingType === 'video' ? 'Play Video Recording' : 'Play Audio Recording'}
                  </p>
                </div>
              )
            ) : (
              <div className="text-slate-500 flex flex-col items-center">
                <VideoCameraIcon className="w-12 h-12 mb-2 opacity-20" />
                <span className="text-sm font-medium">No recording available</span>
              </div>
            )}
          </div>
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/10 backdrop-blur-md text-white text-[11px] font-semibold px-3 py-1.5 rounded-full border border-white/20 shadow-lg">
            <VideoCameraIcon className="w-4 h-4" />
            <span>
              {session.recordingType === 'audio'
                ? 'Audio Replay'
                : 'Video Replay'}
            </span>
          </div>
          <div className="absolute top-4 right-4 text-[11px] font-semibold text-white/80 px-3 py-1 rounded-full border border-white/20 bg-white/5 backdrop-blur-md">
            {session?.startedAt
              ? new Date(session.startedAt).toLocaleDateString()
              : 'Session'}
          </div>
          {!recordingUrl && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent px-5 pt-10 pb-5 pointer-events-none">
              <h4 className="text-white font-bold text-lg">{session?.candidateName || candidateNameFallback || 'Candidate'}</h4>
              <p className="text-slate-300 text-sm truncate">{interviewTitleFallback || 'Interview Session'}</p>
            </div>
          )}
        </div>

        {/* RIGHT: Inner Tabs */}
        <div className="w-full xl:basis-[60%] flex flex-col bg-gradient-to-br from-white via-white to-slate-50 border border-gray-200 rounded-2xl shadow-lg overflow-hidden" style={{ minHeight: '260px', maxHeight: '420px' }}>
          <div className="flex border-b border-gray-100 bg-gray-50/50 shrink-0">
            <button className={`flex-1 py-3.5 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${innerTab === 'questions' ? 'text-indigo-600 bg-white border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} onClick={() => onInnerTabChange && onInnerTabChange('questions')}>
              <ClipboardDocumentCheckIcon className="w-4 h-4" /> Question Analysis
            </button>
            <button className={`flex-1 py-3.5 px-4 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${innerTab === 'transcript' ? 'text-indigo-600 bg-white border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} onClick={() => onInnerTabChange && onInnerTabChange('transcript')}>
              <ChatBubbleLeftRightIcon className="w-4 h-4" /> Transcript
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            {innerTab === 'questions' && (
              <div>
                {/* AI Question Insights */}
                {scored.rubric?.perQuestion &&
                  scored.rubric.perQuestion.length > 0 && (
                    <div className="space-y-2.5">
                      {scored.rubric.perQuestion.map((q, idx) => {
                        const questionKey = `question-${q.index || idx}`;
                        const isExpanded = expanded.has(questionKey);
                        const score = q.score || 0;
                        const scoreColor = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : score > 0 ? '#ea580c' : '#dc2626';
                        const pct = Math.min(score, 100);
                        return (
                          <div key={idx} className="group relative">
                            <button
                              onClick={() => toggleExpanded(questionKey)}
                              className="w-full text-left rounded-xl p-3 transition-all duration-200 hover:bg-gray-50/80"
                              style={{ background: isExpanded ? 'rgb(249,250,251)' : 'transparent' }}
                            >
                              <div className="flex items-start gap-3">
                                {/* Circular Score */}
                                <div className="flex-shrink-0 relative w-12 h-12 flex items-center justify-center">
                                  <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f1f5f9" strokeWidth="3.2" />
                                    <circle cx="18" cy="18" r="15.5" fill="none" stroke={scoreColor} strokeWidth="3.2"
                                      strokeDasharray={`${pct * 0.975} 100`}
                                      strokeLinecap="round"
                                      className="transition-all duration-500"
                                    />
                                  </svg>
                                  <span className="absolute inset-0 flex items-center justify-center text-[13px] font-extrabold" style={{ color: scoreColor }}>{score}</span>
                                </div>
                                {/* Question Text */}
                                <div className="flex-1 min-w-0 pt-0.5">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span className="text-[14px] font-bold text-gray-900">Q{q.index + 1 || idx + 1}</span>
                                    {q.critical && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-bold uppercase tracking-wider">Critical</span>
                                    )}
                                  </div>
                                  <p className="text-[14px] text-gray-700 leading-snug line-clamp-2 font-medium">{q.prompt}</p>
                                </div>
                                {/* Chevron */}
                                <div className="flex-shrink-0 pt-2">
                                  {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                  )}
                                </div>
                              </div>

                            </button>

                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="ml-[52px] mr-2 mt-1 mb-2 space-y-3">
                                {/* Candidate Response */}
                                {q.responses && q.responses.length > 0 && (
                                  <div>
                                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Response</div>
                                    {q.responses.map((response, rIdx) => (
                                      <p key={rIdx} className="text-[13px] text-gray-700 leading-relaxed bg-white rounded-lg p-2.5 border border-gray-100">
                                        {renderResponse(response)}
                                      </p>
                                    ))}
                                  </div>
                                )}

                                {/* Transcript Segment - Mini Timeline */}
                                {(() => {
                                  const segment = deduplicateTranscriptTurns(q.transcriptSegment);
                                  if (segment && segment.length > 0) {
                                    return (
                                      <div>
                                        <div className="text-[12px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                                          <ChatBubbleLeftRightIcon className="h-3 w-3" /> Conversation
                                        </div>
                                        <div className="rounded-xl p-3 max-h-52 overflow-y-auto">
                                          <div>
                                            {segment.map((turn, tIdx) => (
                                              <div key={`seg-${idx}-${tIdx}`} className={`relative flex gap-2.5 pl-5 ${tIdx < segment.length - 1 ? 'pb-3 border-l-[1.5px] border-indigo-200' : ''} ml-[13px]`}>
                                                <div className="flex-shrink-0 z-10 absolute -left-[14px] top-0">
                                                  {turn.role === 'ai' ? (
                                                    <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm ring-1 ring-indigo-100 overflow-hidden">
                                                      <img src="/favicon/favicon-96x96.png" alt="TC" className="w-4 h-4 object-contain" />
                                                    </div>
                                                  ) : (
                                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-sm ring-1 ring-white">
                                                      <span className="text-white text-[8px] font-extrabold">{(session.candidateName || 'C').charAt(0).toUpperCase()}</span>
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="flex-1 min-w-0 pt-0.5">
                                                  <span className="text-[13px] font-bold text-gray-800">
                                                    {turn.role === 'ai' ? 'TalentCrux AI' : (session.candidateName || 'Candidate')}
                                                  </span>
                                                  <p className="text-[14px] text-gray-700 leading-relaxed mt-0.5">{renderTurnText(turn)}</p>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  const evidence =
                                    session.analytics?.detailedAnalysis?.perQuestionEvidence?.find(
                                      (e) => e.questionIndex === (q.index ?? idx)
                                    )?.evidence;
                                  return evidence ? (
                                    <div>
                                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Evidence</div>
                                      <p className="text-[12px] text-gray-600 italic bg-indigo-50/50 rounded-lg p-2.5 border border-indigo-100">
                                        &ldquo;{evidence}&rdquo;
                                      </p>
                                    </div>
                                  ) : null;
                                })()}


                                {/* AI Notes - compact */}
                                {q.notes && (
                                  <div>
                                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">AI Notes</div>
                                    <p className="text-[15px] text-gray-500 leading-relaxed italic">
                                      {(String(q.notes).match(/[^.]+(?:\.|$)/g) || [String(q.notes)])
                                        .map((s) => s.trim())
                                        .filter((s) => !/could not be normalized/i.test(s))
                                        .join(' ')
                                        .trim()}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            )}
            {innerTab === 'transcript' && (
              <div className="py-2">
                {session.transcript && session.transcript.length > 0 ? (
                  (() => {
                    const transcript = deduplicateTranscriptTurns(session.transcript);
                    if (transcript.length === 0) {
                      return <div className="text-center py-12 text-sm text-gray-500">No transcript available</div>;
                    }
                    const groups = [];
                    let current = { role: transcript[0].role, turns: [transcript[0]] };
                    for (let i = 1; i < transcript.length; i++) {
                      if (transcript[i].role === current.role) {
                        current.turns.push(transcript[i]);
                      } else {
                        groups.push(current);
                        current = { role: transcript[i].role, turns: [transcript[i]] };
                      }
                    }
                    groups.push(current);
                    let cumulativeSeconds = 0;
                    const sessionStart = session?.startedAt ? new Date(session.startedAt) : null;
                    return (
                      <div className="relative">
                        <div className="absolute left-[17px] top-8 bottom-8 w-[2px] bg-gradient-to-b from-indigo-300 via-indigo-200 to-gray-200" />
                        <div className="space-y-8">
                          {groups.map((group, groupIdx) => {
                            const startOffset = cumulativeSeconds;
                            const groupDuration = group.turns.reduce((sum, t) => sum + (t.duration || Math.ceil(asText(t.text).length / 15)), 0);
                            cumulativeSeconds += groupDuration;
                            const mm = String(Math.floor(startOffset / 60)).padStart(2, '0');
                            const ss = String(startOffset % 60).padStart(2, '0');
                            return (
                              <div key={`tg-${groupIdx}-${group.role}`} className="relative flex gap-3.5">
                                <div className="flex-shrink-0 z-10">
                                  {group.role === 'ai' ? (
                                    <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-md ring-2 ring-indigo-100 overflow-hidden">
                                      <img src="/favicon/favicon-96x96.png" alt="TalentCrux" className="w-6 h-6 object-contain" />
                                    </div>
                                  ) : (
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md ring-2 ring-white">
                                      <span className="text-white text-[10px] font-extrabold">{(session.candidateName || 'C').charAt(0).toUpperCase()}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 pb-1">
                                  <div className="flex items-center justify-between gap-2 mb-1 pr-2">
                                    <span className="text-[14px] font-bold text-gray-900">
                                      {group.role === 'ai' ? 'TalentCrux AI' : (session.candidateName || 'Candidate')}
                                    </span>
                                    {(() => {
                                      const actualTime = sessionStart ? new Date(sessionStart.getTime() + startOffset * 1000) : null;
                                      const formattedDate = actualTime
                                        ? actualTime.toLocaleDateString('en-US', { timeZone: 'Asia/Kolkata', weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                                        : '';
                                      const formattedTime = actualTime
                                        ? actualTime.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' })
                                        : '';
                                      return (
                                        <UITooltipProvider>
                                          <UITooltip>
                                            <UITooltipTrigger asChild>
                                              <span className="text-[11px] text-gray-400 font-medium tabular-nums cursor-help hover:text-indigo-600 transition-colors border-b border-dashed border-gray-300">
                                                {mm}:{ss}
                                              </span>
                                            </UITooltipTrigger>
                                            <UITooltipContent side="top" className="bg-gray-900 border-gray-800 text-white text-xs p-2.5 rounded-lg shadow-lg z-[9999]">
                                              <div className="font-semibold text-indigo-400 mb-1">Recorded (IST)</div>
                                              {actualTime ? (
                                                <div className="space-y-0.5 font-medium">
                                                  <div>Date: {formattedDate}</div>
                                                  <div>Time: {formattedTime}</div>
                                                </div>
                                              ) : (
                                                <div className="text-gray-400">Timestamp unavailable</div>
                                              )}
                                            </UITooltipContent>
                                          </UITooltip>
                                        </UITooltipProvider>
                                      );
                                    })()}
                                  </div>
                                  <div className="space-y-1.5">
                                    {group.turns.map((turn, turnIdx) => (
                                      <p key={`turn-${groupIdx}-${turnIdx}`} className="text-[14px] text-gray-700 leading-relaxed">
                                        {renderTurnText(turn)}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-12 text-sm text-gray-500">No transcript available</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ BOTTOM: AI Score Cards ═══ */}
      {(() => {
        const val = scored.scores?.overall ?? scored.score;
        const scoreColor = val >= 80 ? '#059669' : val >= 60 ? '#d97706' : '#dc2626';
        const scoreBg = val >= 80 ? 'from-emerald-50 to-green-50' : val >= 60 ? 'from-amber-50 to-yellow-50' : 'from-red-50 to-rose-50';
        const pct = Math.min(val || 0, 100);

        const perSection = scored.scores?.perSection;
        const sectionData = perSection
          ? (perSection instanceof Map ? Object.fromEntries(perSection) : (typeof perSection === 'object' ? perSection : {}))
          : {};
        let sectionEntries = Object.entries(sectionData).filter(([s, v]) => typeof v === 'number' && s !== 'intro' && s !== 'wrap_up');

        // Fallback for rubric/competency-based interviews if sectionEntries only contains generic 'core' or is empty
        if (
          sectionEntries.length === 0 ||
          (sectionEntries.length === 1 && sectionEntries[0][0] === 'core')
        ) {
          let perQ = scored.scores?.perQuestion;
          if (!Array.isArray(perQ) || perQ.length === 0) {
            perQ = scored.rubric?.perQuestion || [];
          }
          const questionRubricMap = new Map();

          if (Array.isArray(scored.rubricSelections) && scored.rubricSelections.length > 0) {
            let gIdx = 0;
            for (const sel of scored.rubricSelections) {
              const rName = sel.rubricName || 'Core';
              const indices = Array.isArray(sel.selectedQuestionIndices) ? sel.selectedQuestionIndices : [];
              for (let k = 0; k < indices.length; k++) {
                questionRubricMap.set(gIdx, rName);
                gIdx++;
              }
            }
          } else if (Array.isArray(interviewDetails?.rubrics) && interviewDetails.rubrics.length > 0) {
            // Try prompt matching against interviewDetails.rubrics for older sessions
            perQ.forEach((pq, qIdx) => {
              const pText = (pq.prompt || '').toString().trim().toLowerCase();
              if (!pText) return;
              for (const r of interviewDetails.rubrics) {
                const bank = Array.isArray(r.questionBank) ? r.questionBank : [];
                const match = bank.some((q) => {
                  const qText = (q.prompt || q.text || q.question || '').toString().trim().toLowerCase();
                  return qText && (qText === pText || pText.includes(qText) || qText.includes(pText));
                });
                if (match) {
                  questionRubricMap.set(qIdx, r.name || 'Core');
                  break;
                }
              }
            });

            // Positional index fallback if prompt matching didn't match all questions
            if (questionRubricMap.size < perQ.length) {
              let qIndexCursor = 0;
              for (const r of interviewDetails.rubrics) {
                const pickCount = Math.max(1, r.questionsPerInterview || 1);
                const rName = r.name || 'Core';
                for (let c = 0; c < pickCount; c++) {
                  if (!questionRubricMap.has(qIndexCursor)) {
                    questionRubricMap.set(qIndexCursor, rName);
                  }
                  qIndexCursor++;
                }
              }
            }
          }

          if (questionRubricMap.size > 0 && perQ.length > 0) {
            const rubricScores = {};
            const rubricCounts = {};
            perQ.forEach((pq, qIdx) => {
              const idx = typeof pq.index === 'number' ? pq.index : (typeof pq.questionIndex === 'number' ? pq.questionIndex : qIdx);
              const sc = typeof pq.score === 'number' ? pq.score : pq.llmScore;
              const rName = questionRubricMap.get(idx) || questionRubricMap.get(qIdx);
              if (rName && typeof sc === 'number') {
                rubricScores[rName] = (rubricScores[rName] || 0) + sc;
                rubricCounts[rName] = (rubricCounts[rName] || 0) + 1;
              }
            });

            const computedRubricEntries = Object.entries(rubricScores).map(([rName, totalScore]) => [
              rName,
              Math.round(totalScore / rubricCounts[rName]),
            ]);

            if (computedRubricEntries.length > 0) {
              sectionEntries = computedRubricEntries;
            }
          }
        }

        const analytics = scored.analytics || {};
        const strengths = analytics.strengths || [];
        const improvements = analytics.areasForImprovement || [];
        const redFlags = analytics.redFlags || [];
        const standout = analytics.standoutMoments || [];

        // Donut chart colors
        const chartColors = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6'];

        const commScore = scored.scores?.communication;
        const hasCommSection = sectionEntries.some(([k]) => k.toLowerCase().includes('communication'));
        const radarData = [
          ...sectionEntries.map(([key, value]) => ({
            subject: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            score: value,
            fullMark: 100,
          })),
          ...(commScore != null && !hasCommSection ? [{ subject: 'Communication', score: commScore, fullMark: 100 }] : []),
        ];

        if (typeof renderScoreCards === 'function') {
          return renderScoreCards({ overall: val, areas: radarData, strengths, improvements, redFlags, standout });
        }

        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 mt-2 border-t border-gray-100">

            {/* Card 1: Overall Score */}
            <div className={`rounded-2xl p-5 bg-gradient-to-br ${scoreBg} border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center`}>
              <div className="relative mb-3" style={{ width: 120, height: 120 }}>
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke={scoreColor} strokeWidth="2.5"
                    strokeDasharray={`${pct * 0.975} 100`}
                    strokeLinecap="round"
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-extrabold" style={{ color: scoreColor }}>{val ?? '—'}</span>
                  <span className="text-[10px] text-gray-400 font-medium">/ 100</span>
                </div>
              </div>
              <p className="text-sm font-bold text-gray-800 mb-1">AI Interview Score</p>
              <p className="text-[11px] text-gray-500 mb-3">
                {val >= 80 ? 'Excellent performance' : val >= 60 ? 'Good, room to improve' : 'Needs improvement'}
              </p>
              {/* Shortlist button removed per request */}
            </div>

            {/* Card 2: Score Breakdown — Animated Radar Chart */}
            <div className="rounded-2xl p-5 bg-white border border-gray-100 shadow-sm flex flex-col items-center">
              <p className="text-sm font-bold text-gray-800 mb-1">AI Score Breakdown</p>
              {(() => {
                if (radarData.length === 0) return <p className="text-xs text-gray-400 italic mt-8">No section data</p>;
                return (
                  <div className="w-full flex flex-col items-center">
                    <ResponsiveContainer width="100%" height={220}>
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                        <PolarGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                        <PolarAngleAxis
                          dataKey="subject"
                          tick={{ fontSize: 9, fontWeight: 600, fill: '#64748b' }}
                        />
                        <PolarRadiusAxis
                          angle={90}
                          domain={[0, 100]}
                          tick={false}
                          axisLine={false}
                        />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fill="url(#radarGradient)"
                          fillOpacity={0.35}
                          dot={{ r: 4, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
                          animationBegin={200}
                          animationDuration={1200}
                          animationEasing="ease-out"
                        />
                        <defs>
                          <linearGradient id="radarGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#818cf8" stopOpacity={0.6} />
                            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.15} />
                          </linearGradient>
                        </defs>
                        <Tooltip
                          contentStyle={{ borderRadius: 12, fontSize: 11, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                          formatter={(v) => [`${v}%`, 'Score']}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-1">
                      {radarData.map((d, i) => (
                        <div key={d.subject} className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: chartColors[i % chartColors.length] }} />
                          <span className="text-[10px] text-gray-500 font-medium">{d.subject}</span>
                          <span className="text-[10px] font-bold text-gray-800">{d.score}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Card 3: AI Insights */}
            <div className="rounded-2xl p-5 bg-white border border-gray-100 shadow-sm flex flex-col">
              <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">✦</span> AI Insights
              </p>
              <div className="flex-1 overflow-y-auto max-h-64 pr-1" style={{ scrollbarWidth: 'thin' }}>
                {[
                  ...strengths.slice(0, 3).map(s => ({ text: s, type: 'strength' })),
                  ...improvements.slice(0, 2).map(s => ({ text: s, type: 'improve' })),
                  ...redFlags.slice(0, 2).map(s => ({ text: s, type: 'flag' })),
                  ...standout.slice(0, 2).map(s => ({ text: s, type: 'standout' })),
                ].map((item, idx) => {
                  const config = {
                    strength: { label: 'Strength', bg: 'bg-emerald-500', text: 'text-emerald-700', dot: 'bg-emerald-400' },
                    improve: { label: 'Improve', bg: 'bg-amber-500', text: 'text-amber-700', dot: 'bg-amber-400' },
                    flag: { label: 'Flag', bg: 'bg-red-500', text: 'text-red-700', dot: 'bg-red-400' },
                    standout: { label: 'Standout', bg: 'bg-indigo-500', text: 'text-indigo-700', dot: 'bg-indigo-400' },
                  }[item.type];
                  return (
                    <div key={idx} className={`flex items-center gap-3 py-2.5 ${idx > 0 ? 'border-t border-gray-50' : ''}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11.5px] text-gray-700 leading-relaxed">{item.text}</p>
                      </div>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-semibold text-white ${config.bg}`}>
                        {config.label}
                      </span>
                    </div>
                  );
                })}
                {strengths.length === 0 && improvements.length === 0 && redFlags.length === 0 && standout.length === 0 && (
                  <p className="text-xs text-gray-400 italic text-center py-4">No insights available</p>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
